import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid, GridCellParams } from "@mui/x-data-grid";
import get from "lodash/get";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import {
  default as category,
  default as categoryService,
} from "../../services/category";
import { rupiah } from "../../utils/currency";
import cookie from "../../utils/useCookie";

const NoOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Nothing to show
    </Typography>
  </Box>
);

// Updated modal style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 600 },
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

// Form field component for reusability
interface FormFieldProps {
  label: string;
  name: string;
  control: any;
  type?: string;
  multiline?: boolean;
  rows?: number;
}

const FormField = ({
  label,
  name,
  control,
  type = "text",
  multiline = false,
  rows = 1,
}: FormFieldProps) => (
  <FormControl fullWidth sx={{ mb: 2.5 }}>
    <FormLabel required sx={{ mb: 0.5, fontWeight: 500 }}>
      {label}
    </FormLabel>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          type={type}
          multiline={multiline}
          rows={rows}
          size="small"
          fullWidth
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
            },
          }}
        />
      )}
    />
  </FormControl>
);

// Add this new component for mobile card view
interface MobileCardViewProps {
  data: Array<{
    id: number;
    name: string;
    price: number;
    available_count: number;
    booked_count: number;
    sold_count: number;
  }>;
  onEdit: (params: any) => void;
  onDelete: (id: number) => void;
  onClickDetails: (row: any) => void;
}

const MobileCardView = ({
  data,
  onEdit,
  onDelete,
  onClickDetails,
}: MobileCardViewProps) => (
  <Stack spacing={2}>
    {data.map((row) => (
      <Card key={row.id} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => onClickDetails(row)}
            >
              {row.name}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onEdit({ row })}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Price:{" "}
              <Typography component="span" fontWeight="500">
                {rupiah(row.price)}
              </Typography>
            </Typography>

            <Typography variant="body2" fontWeight="500">
              Ticket Statistics:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`Available: ${row.available_count}`}
                color="success"
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Booked: ${row.booked_count}`}
                color="warning"
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Sold: ${row.sold_count}`}
                color="info"
                size="small"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Stack>
      </Card>
    ))}
  </Stack>
);

export default function Category(params: any) {
  const { id, event_id } = params;
  const user = JSON.parse(cookie.getCookie("USER") || "");
  const notification = useNotification();
  const location = useLocation();
  const path = location.pathname.split("/");
  const event_slug = path.pop() || "";

  const form = useForm({
    defaultValues: {
      name: "",
      price: 0,
      total_ticket: 0,
      start_time: "",
      end_time: "",
      username: user?.name,
      event_id: event_id,
      id: "",
      description: "",
      schedule: "",
      is_active: false,
      show_masked_sold: false,
      masked_sold: 0,
      masked_invitation: 0,
      masked_available: 0,
      order: 0,
    },
  });

  useEffect(() => {
    form.setValue("event_id", event_id);
  }, [event_id]);

  const listCategory = useQuery("list-category", () =>
    category.getCategoryByEventId({ event_id: id })
  );

  const createCategory = useMutation(
    "create-category",
    categoryService.createCategory,
    {
      onSuccess: () => {
        listCategory.remove();
        listCategory.refetch();
        form.reset();
        notification.onOpen({
          message: "Category was successfully created!",
          type: "success",
          position: "top",
        });
      },
    }
  );

  const updateCategory = useMutation(
    "update-category",
    categoryService.updateCategory,
    {
      onSuccess: () => {
        listCategory.remove();
        listCategory.refetch();
        form.reset();
        notification.onOpen({
          message: "Category was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.error !== ""
              ? error.response.data.error
              : "Something went wrong",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const deleteCategory = useMutation(
    "delete-category",
    categoryService.deleteCategory,
    {
      onSuccess: () => {
        listCategory.remove();
        listCategory.refetch();
        notification.onOpen({
          message: "Category was successfully deleted!",
          type: "success",
          position: "top",
        });
      },
    }
  );

  useEffect(() => form.setValue("event_id", event_id), [event_id]);

  const alert = useAlert();

  const onDelete = (id: number) => {
    alert.onClose();
    deleteCategory.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete category?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onClickEdit = (params: any) => {
    setOpen(true);
    form.setValue("id", params.row.id);
    form.setValue("name", params.row.name);
    form.setValue("price", params.row.price);
    form.setValue("total_ticket", params.row.total_ticket);
    form.setValue("description", params.row.description);
    form.setValue("schedule", params.row.schedule);
    form.setValue("is_active", params.row.is_active);
    form.setValue("show_masked_sold", params.row.show_masked_sold);
    form.setValue("masked_sold", params.row.masked_sold);
    form.setValue("masked_invitation", params.row.masked_invitation);
    form.setValue("event_id", event_id);
    form.setValue("masked_available", params.row.masked_available);
    form.setValue("order", params.row.order || 0);

    const formatDateToLocal = (dateString: string) => {
      const date = new Date(dateString);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const formattedStartTime = formatDateToLocal(params.row.start_time);
    const formattedEndTime = formatDateToLocal(params.row.end_time);

    form.setValue("start_time", formattedStartTime);
    form.setValue("end_time", formattedEndTime);
  };

  const [open, setOpen] = useState(false);

  const onSubmit = (values: any) => {
    if (values.id) {
      updateCategory.mutate(values);
    } else {
      createCategory.mutate(values);
    }
    setOpen(false);
  };

  const navigate = useNavigate();
  const handleOnCellClick = (params: GridCellParams) => {
    const { row } = params;
    navigate(`/admin/event/${event_slug}/category/${row.id}`, {
      state: { ...row, event_slug },
    });
  };

  const handleCloseModal = () => {
    setOpen(false);
    form.reset();
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Add this handler for mobile card view
  const handleCardClick = (row: any) => {
    navigate(`/admin/event/${event_slug}/category/${row.id}`, {
      state: { ...row, event_slug },
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Card elevation={2} sx={{ mb: 3, p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight="bold">
            Event Categories
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setOpen(true)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
          >
            Add Category
          </Button>
        </Stack>

        {isMobile ? (
          <MobileCardView
            data={get(listCategory, "data.data", [])}
            onEdit={onClickEdit}
            onDelete={onClickDelete}
            onClickDetails={handleCardClick}
          />
        ) : (
          <DataGrid
            hideFooterPagination
            autoHeight
            disableSelectionOnClick
            disableColumnMenu
            loading={listCategory.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listCategory, "data.data", [])}
            rowCount={get(listCategory, "data.data", []).length}
            components={{
              NoRowsOverlay: NoOverlay,
              LoadingOverlay: () => (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress />
                </Box>
              ),
            }}
            columns={[
              {
                field: "name",
                headerName: "Name",
                flex: 1,
                minWidth: 200,
                renderCell: (params) => (
                  <Tooltip title="Click to view details" arrow>
                    <Stack
                      onClick={() => handleOnCellClick(params)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <Typography color="primary.main">
                        {params.value}
                      </Typography>
                    </Stack>
                  </Tooltip>
                ),
              },
              {
                field: "price",
                headerName: "Price",
                flex: 0.8,
                minWidth: 120,
                headerAlign: "right",
                align: "right",
                renderCell: (params) => (
                  <Typography fontWeight="500">
                    {rupiah(params.value)}
                  </Typography>
                ),
              },
              {
                field: "ticket_stats",
                headerName: "Ticket Statistics",
                flex: 1.2,
                minWidth: 250,
                renderCell: (params) => (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      label={`Available: ${params.row.available_count}`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Booked: ${params.row.booked_count}`}
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Sold: ${params.row.sold_count}`}
                      color="info"
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                ),
              },
              {
                field: "actions",
                headerName: "Actions",
                sortable: false,
                filterable: false,
                width: 160,
                renderCell: (params) => (
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Edit category" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onClickEdit(params)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete category" arrow>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onClickDelete(params.row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ),
              },
            ]}
            sx={{
              border: "none",
              "& .MuiDataGrid-root": {
                backgroundColor: "background.paper",
                borderRadius: 1,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
                padding: "12px 16px",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "background.neutral",
                borderRadius: "8px 8px 0 0",
                borderBottom: "2px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                color: "text.primary",
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                "&:nth-of-type(odd)": {
                  backgroundColor: "action.selected",
                },
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
            }}
          />
        )}
      </Card>

      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            variant="h5"
            component="h2"
            fontWeight="bold"
            sx={{ mb: 3 }}
          >
            {!form.getValues().id ? "Create New Category" : "Edit Category"}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <FormField label="Category Name" name="name" control={form.control} />

          <FormField
            label="Description"
            name="description"
            control={form.control}
            multiline
            rows={3}
          />

          <FormField label="Schedule" name="schedule" control={form.control} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mb: 2.5 }}
          >
            <FormField
              label="Price"
              name="price"
              control={form.control}
              type="number"
            />
            <FormField
              label="Total Tickets"
              name="total_ticket"
              control={form.control}
              type="number"
            />
            <FormField
              label="Order"
              name="order"
              control={form.control}
              type="number"
            />
          </Stack>

          {user?.role_id === 3 && <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <FormField
              label="Masked Sold"
              name="masked_sold"
              control={form.control}
              type="number"
            />
            <FormField
              label="Masked Invitation"
              name="masked_invitation"
              control={form.control}
              type="number"
            />
            <FormField
              label="Masked Available"
              name="masked_available"
              control={form.control}
              type="number"
            />
          </Stack>}
          

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <FormField
              label="Start Time"
              name="start_time"
              control={form.control}
              type="datetime-local"
            />
            <FormField
              label="End Time"
              name="end_time"
              control={form.control}
              type="datetime-local"
            />
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            {user?.role_id === 3 && (
              <FormControl fullWidth sx={{ mb: 2.5 }}>
                <FormLabel required sx={{ mb: 0.5, fontWeight: 500 }}>
                  Show Masked Sold
                </FormLabel>
                <Controller
                  name="show_masked_sold"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={form.getValues().show_masked_sold}
                    />
                  )}
                />
              </FormControl>
            )}
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <FormLabel required sx={{ mb: 0.5, fontWeight: 500 }}>
                Is Active
              </FormLabel>
              <Controller
                name="is_active"
                control={form.control}
                render={({ field }) => (
                  <Checkbox {...field} checked={form.getValues().is_active} />
                )}
              />
            </FormControl>
          </Stack>
          <Button
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
          >
            Submit
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}
